/**
 * Палитра кабинета — единый источник цвета для TypeScript.
 * Из неё собираются токены Ant Design (`src/providers/theme.provider.tsx`).
 * CSS-переменные в `src/styles/theme.scss` обязаны совпадать с ней:
 * ключ `brandPrimary` ↔ `--color-brand-primary`. Совпадение проверяет тест `src/tests/styles/palette.test.ts`.
 */

/** Бренд и статусы — одинаковые в обеих схемах. */
const BRAND_AND_STATUS = {
  brandPrimary: '#1677ff',
  brandPrimaryHover: '#4096ff',
  brandPrimaryActive: '#0958d9',
  brandOnPrimary: '#fff',

  accentGreen: '#52c41a',
  accentAmber: '#faad14',
  accentBlue: '#1677ff',
  accentRed: '#ff4d4f',
  onAccent: '#fff',

  statusSuccess: '#52c41a',
  statusWarning: '#faad14',
  statusInfo: '#1677ff',
  statusError: '#ff4d4f',

  overlayScrim: 'rgba(0, 0, 0, 0.45)'
};

/** Панель навигации (drawer) — тёмная в обеих схемах, текст на ней всегда светлый. */
const SIDER_TEXT = {
  siderText: 'rgba(255, 255, 255, 0.65)',
  siderTextStrong: '#fff',
  siderTextMuted: 'rgba(255, 255, 255, 0.55)',
  siderHover: 'rgba(255, 255, 255, 0.08)',
  siderBorder: 'rgba(255, 255, 255, 0.1)'
};

export const LIGHT_PALETTE = {
  ...BRAND_AND_STATUS,
  ...SIDER_TEXT,

  brandTint: 'rgba(22, 119, 255, 0.1)',
  brandTintStrong: 'rgba(22, 119, 255, 0.18)',
  // надпись логотипа; знак — в фирменных цветах самого SVG
  brandLogoText: '#154a79',

  siderBg: '#0e2b52',

  background: '#f5f5f5',
  surface: '#fff',
  surfaceMuted: '#fafafa',

  textPrimary: 'rgba(0, 0, 0, 0.88)',
  textSecondary: 'rgba(0, 0, 0, 0.65)',
  textTertiary: 'rgba(0, 0, 0, 0.45)',
  textDisabled: 'rgba(0, 0, 0, 0.25)',
  textInverse: '#fff',

  border: '#f0f0f0',
  borderStrong: '#d9d9d9',

  hover: 'rgba(0, 0, 0, 0.04)'
};

export type TPalette = typeof LIGHT_PALETTE;

export const DARK_PALETTE: TPalette = {
  ...LIGHT_PALETTE,

  brandTint: 'rgba(22, 119, 255, 0.16)',
  brandTintStrong: 'rgba(22, 119, 255, 0.28)',
  // надпись логотипа; знак — в фирменных цветах самого SVG
  brandLogoText: '#fff',

  siderBg: '#0a1f3b',

  background: '#141414',
  surface: '#1f1f1f',
  surfaceMuted: '#262626',

  textPrimary: 'rgba(255, 255, 255, 0.85)',
  textSecondary: 'rgba(255, 255, 255, 0.65)',
  textTertiary: 'rgba(255, 255, 255, 0.45)',
  textDisabled: 'rgba(255, 255, 255, 0.25)',
  textInverse: 'rgba(0, 0, 0, 0.88)',

  border: '#303030',
  borderStrong: '#424242',

  hover: 'rgba(255, 255, 255, 0.08)'
};

/** Палитра для эффективной схемы темы. */
export const getPalette = (theme: 'light' | 'dark'): TPalette =>
  theme === 'dark' ? DARK_PALETTE : LIGHT_PALETTE;
